import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Card, Grid, Stack } from '@mui/material';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';

import { FormProvider, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';
import { IGeneratRentPaymentList } from 'src/@types/Choqorak/MainTables/RentPayment.ts/RentPayment';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';

// ----------------------------------------------------------------------

export default observer(function GenerateRentPaymentList() {
  const { RentPaymentStore } = useStore();
  const { translate } = useLocales();
  const {
    createRentPayment,
    editMode,
    clearSelectedRentPayment,
    setOpenCloseGeneratRentListDialog,
    GeneratRentPaymentList,
  } = RentPaymentStore;

  const navigate = useNavigate();
  const [year, setYear] = useState<string>(''); // State for the year
  const [month, setMonth] = useState<string>(''); // State for the month
  const { enqueueSnackbar } = useSnackbar();

  const NewRentPaymentSchema = Yup.object().shape({
    // year: Yup.string().required(`${translate('Validation.Year')}`),
  });

  const defaultValues = useMemo<IGeneratRentPaymentList>(() => ({

  }), []);

  const methods = useForm<IGeneratRentPaymentList>({
    resolver: yupResolver(NewRentPaymentSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    control,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data: IGeneratRentPaymentList) => {
    const validYear = year !== undefined && !isNaN(Number(year));
    const validMonth = month !== undefined && !isNaN(Number(month));

    const newData = {
      year: validYear ? Number(year) : undefined,
      month: validMonth ? Number(month) : undefined,
    };

    try {
      await GeneratRentPaymentList(newData);
      setOpenCloseGeneratRentListDialog();
      reset();
      enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
      navigate(PATH_DASHBOARD.RentPayment.list);
    } catch (err: any) {
      try {
        const json = JSON.parse(err?.request?.response);
        setError('afterSubmit', { ...err, message: json.message });
      } catch {
        setError('afterSubmit', { type: 'manual', message: err.message || 'Unexpected error' });
      }
    }
  };


  useEffect(() => {
    if (editMode) {
      reset(defaultValues);
    }
    if (!editMode) {
      reset(defaultValues);
    }
  }, [reset, editMode, defaultValues]);

  const yearsDate = watch('year'); // Watching the 'years' field

  useEffect(() => {
    if (yearsDate) {
      const changeDateToDateFormat = new Date(yearsDate);

      // Extract Persian year and month
      const persianYear = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
        year: 'numeric',
      });
      const persianMonth = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
        month: 'numeric',
      });

      // Convert Persian numerals to Western numerals
      const westernYear = persianYear.replace(/[۰-۹]/g, (digit) =>
        '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
      );
      const westernMonth = persianMonth.replace(/[۰-۹]/g, (digit) =>
        '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
      );

      // Update state with Western year and month
      setYear(westernYear);
      setMonth(westernMonth);
    }
  }, [yearsDate]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <LocalizDatePicker
                name="year"
                // label={translate('Employee.date')}

                label="Year & Month"
                control={control}
                showAsterisk={true}
                views={['year', 'month']}
              />
              {/* <RHFTextField
                name="year"
                label={translate('GeneralFields.year')}
                showAsterisk={true}
                autoFocus
              />
              <RHFTextField
                name="month"
                label={translate('GeneralFields.month')}
                showAsterisk={true}
              /> */}
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  setOpenCloseGeneratRentListDialog();
                  reset();
                }}
              >
                {/* {translate('CRUD.BackToList')} */}
                Cancel
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
